/**
 * Shared IDP environment resolution for all api_key → OAuth token exchanges.
 *
 * Both AgentCoreAPI (agent scopes) and OrganizationsAPI (api.internal.basic)
 * hit the same IDP /connect/token endpoint with the same web-exchange clients,
 * so the env→url and env→client-id mappings live here once instead of being
 * copied (and drifting) per call site.
 */

/**
 * Derive the canonical env key from the base_url returned by v4/login_check.
 * Falls back to apiEnv when baseUrl is not set.
 *
 * Examples:
 *   https://uat-temp-api.tigertext.me    → 'uat'
 *   https://api.tigertext.me             → 'production'
 *   https://canary-a-api.tigertext.me    → 'production'
 *   https://stag-devapi.tigertext.me     → 'uat'   (staging shares the uat IDP)
 *   https://prod-dr-api.tigertext.me     → 'production'
 *   https://env7-devapi.tigertext.xyz    → 'env7'
 */
export function resolveEnv(baseUrl: unknown, apiEnv: string): string {
  if (typeof baseUrl === 'string') {
    try {
      const prefix = new URL(baseUrl).hostname.split('.')[0].split('-')[0];
      return canonicalize(prefix);
    } catch {
      // malformed baseUrl — fall through to apiEnv
    }
  }
  return canonicalize(apiEnv || 'production');
}

// Map host-prefix / apiEnv aliases onto the canonical env whose IDP they share.
function canonicalize(env: string): string {
  switch (env) {
    case 'api':
    case 'temp':
    case 'canary':
    case 'prod':
    case 'production':
      return 'production';
    case 'stag':
      return 'uat';
    default:
      return env; // 'uat', 'env7', 'env8', …
  }
}

const IDP_BASE_URLS: Record<string, string> = {
  production: 'https://idp.tigerconnect.com',
  uat: 'https://idp.uat.tigerconnect.com',
  env7: 'https://idp.env7.tigerconnect.com',
};

const IDP_CLIENT_IDS: Record<string, string> = {
  production: 'web-exchange-prod',
  uat: 'web-exchange-uat',
  env7: 'web-exchange-dev',
};

/** IDP base URL for an env (no trailing path). */
export function getIdpUrl(env: string): string {
  return IDP_BASE_URLS[env] || `https://idp.${env}.tigerconnect.com`;
}

/** web-exchange client id used for the api_key grant in an env. */
export function getIdpExchangeClientId(env: string): string {
  return IDP_CLIENT_IDS[env] || 'web-exchange-dev';
}

/**
 * Extract the `exp` claim (ms) from a JWT payload without verifying the signature.
 * Returns null if the token is malformed or has no `exp` claim.
 */
export function parseJwtExpiry(token: string): number | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    // JWT uses base64url; normalize to base64 then decode
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const json =
      typeof Buffer !== 'undefined'
        ? Buffer.from(base64, 'base64').toString('utf-8')
        : atob(base64);
    const { exp } = JSON.parse(json);
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}
